import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, MessageCircle, Trash2, Loader2, Trophy } from 'lucide-react';
import LevelBadge, { ExpProgressBar } from '../components/LevelBadge';

const Map = ({ user, onBack, onOpenMessages }) => {
  const [nearbyPlayers, setNearbyPlayers] = useState([]);
  const [hasLocation, setHasLocation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [locationStatus, setLocationStatus] = useState(null);
  const [locationInput, setLocationInput] = useState('');
  const [showLocationForm, setShowLocationForm] = useState(false);

  useEffect(() => {
    checkLocationStatus();
    fetchNearbyPlayers();
  }, []);

  const checkLocationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/location/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasLocation(response.data.has_location);
      setLocationStatus(response.data);
    } catch (error) {
      console.error('Error checking location status:', error);
    }
  };

  const fetchNearbyPlayers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/location/nearby`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNearbyPlayers(response.data.nearby_players || []);
      setHasLocation(response.data.has_location);
    } catch (error) {
      console.error('Error fetching nearby players:', error);
      toast.error('Failed to load nearby players');
    } finally {
      setLoading(false);
    }
  };

  const handlePostLocation = async () => {
    if (!locationInput.trim()) {
      toast.error('Please enter a city, address, or zip code');
      return;
    }

    setPosting(true);
    
    try {
      // Use geocoding API to convert address to coordinates
      const geocodeUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationInput)}&limit=1`;
      
      const geocodeResponse = await fetch(geocodeUrl, {
        headers: {
          'User-Agent': 'Beypointer App'
        }
      });
      
      const geocodeData = await geocodeResponse.json();
      
      if (!geocodeData || geocodeData.length === 0) {
        toast.error('Location not found. Please try a different address or zip code.');
        setPosting(false);
        return;
      }
      
      const { lat, lon } = geocodeData[0];
      
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/location/post`,
        null,
        {
          params: {
            latitude: parseFloat(lat),
            longitude: parseFloat(lon)
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Location posted! Valid for 30 days');
      setHasLocation(true);
      setShowLocationForm(false);
      setLocationInput('');
      await fetchNearbyPlayers();
      await checkLocationStatus();
    } catch (error) {
      console.error('Error posting location:', error);
      toast.error('Failed to post location');
    } finally {
      setPosting(false);
    }
  };

  const handleRemoveLocation = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/location/delete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Location removed');
      setHasLocation(false);
      setNearbyPlayers([]);
      setLocationStatus(null);
    } catch (error) {
      console.error('Error removing location:', error);
      toast.error('Failed to remove location');
    }
  };

  const handleMessagePlayer = (playerUsername) => {
    // Navigate to messages and open chat with this player
    onOpenMessages(playerUsername);
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div 
      className="h-screen text-white p-4 relative overflow-hidden"
      style={{
        backgroundImage: user.chat_list_background ? `url(${user.chat_list_background})` : 'linear-gradient(to bottom right, rgb(15, 23, 42), rgb(30, 41, 59), rgb(15, 23, 42))',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Dark overlay for better text visibility */}
      {user.chat_list_background && (
        <div className="absolute inset-0 bg-black/30 z-0"></div>
      )}
      
      {/* Content wrapper with z-index */}
      <div className="relative z-10 h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-cyan-400 digital-font flex items-center gap-2">
            <MapPin className="w-6 h-6" />
            FIND BLADERS
          </h1>
        </div>
      </div>

      {/* Your Profile - When Location is Active */}
      {hasLocation && (
        <div className="mb-4 space-y-3">
          <p className="text-slate-400 text-sm">Your Location:</p>
          
          {/* User Profile Card - Leaderboard Style */}
          <div 
            className="leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-3 relative"
            style={(user.banner_image && user.banner_image.trim() !== '') ? {
              backgroundImage: `url(${user.banner_image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            } : {}}
          >
            {/* Overlay for better text visibility when banner is set */}
            {user.banner_image && user.banner_image.trim() !== '' && (
              <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
            )}
            
            {/* Level Badge */}
            <LevelBadge level={user.level || 1} small={true} verified={user.has_made_first_purchase} />
            
            {/* Remove Button - Top Right Corner */}
            <Button
              onClick={handleRemoveLocation}
              variant="ghost"
              size="sm"
              className="absolute top-0 right-0 z-20 text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0 rounded-tr-lg"
              title="Remove location"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Profile Picture */}
                {user.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt={user.username}
                    className="w-12 h-12 rounded-full object-cover border-2"
                    style={{ borderColor: user.circle_color || '#475569' }}
                  />
                ) : (
                  <div 
                    className="w-12 h-12 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-300 text-sm font-semibold"
                    style={{ borderColor: user.circle_color || '#475569' }}
                  >
                    {user.username.charAt(0).toUpperCase()}
                  </div>
                )}
                
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${(user.banner_image && user.banner_image.trim() !== '') ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>
                      {user.username}
                    </span>
                    <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                      📍 Active
                    </span>
                  </div>
                  {locationStatus && (
                    <p className={`text-xs mt-1 ${(user.banner_image && user.banner_image.trim() !== '') ? 'text-white/80 drop-shadow' : 'text-slate-400'}`}>
                      Posted {formatDate(locationStatus.posted_at)} • Expires {formatDate(locationStatus.expires_at)}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Battle Points */}
              <span className={`digital-font font-semibold relative z-10 ${(user.banner_image && user.banner_image.trim() !== '') ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                {user.battle_points} BP
              </span>
            </div>
            
            {/* Trophy System - Top Right */}
            <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
              <div className="flex items-center gap-0.5">
                <Trophy className="w-3 h-3 text-yellow-500" />
                <span className="text-[10px] font-bold text-white drop-shadow">{user.gold_trophies || 0}</span>
              </div>
              <div className="flex items-center gap-0.5">
                <Trophy className="w-3 h-3 text-slate-300" />
                <span className="text-[10px] font-bold text-white drop-shadow">{user.silver_trophies || 0}</span>
              </div>
              <div className="flex items-center gap-0.5">
                <Trophy className="w-3 h-3 text-amber-600" />
                <span className="text-[10px] font-bold text-white drop-shadow">{user.bronze_trophies || 0}</span>
              </div>
            </div>
            
            {/* EXP Progress Bar */}
            <ExpProgressBar level={user.level || 1} exp={user.exp || 0} />
          </div>
          
        </div>
      )}

      {/* Main Content */}
      {!hasLocation ? (
        // Prompt to post location
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-center max-w-md w-full px-4">
            <div className="w-24 h-24 mx-auto mb-6 bg-cyan-500/10 rounded-full flex items-center justify-center">
              <MapPin className="w-12 h-12 text-cyan-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Find Bladers Near You!</h2>
            <p className="text-slate-400 mb-6">
              Post your location to see other bladers within 25 miles who want to battle.
              Your location is rounded to ~1 mile for privacy.
            </p>
            
            {!showLocationForm ? (
              <Button
                onClick={() => setShowLocationForm(true)}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-3 text-lg"
              >
                <MapPin className="w-5 h-5 mr-2" />
                Post My Location
              </Button>
            ) : (
              <div className="space-y-4">
                <Input
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePostLocation()}
                  placeholder="Enter city, address, or zip code..."
                  className="bg-slate-800 border-slate-700 text-white text-center"
                  disabled={posting}
                />
                <div className="flex gap-2 justify-center">
                  <Button
                    onClick={handlePostLocation}
                    disabled={posting || !locationInput.trim()}
                    className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
                  >
                    {posting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Posting...
                      </>
                    ) : (
                      'Post Location'
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowLocationForm(false);
                      setLocationInput('');
                    }}
                    variant="ghost"
                    className="text-slate-400 hover:text-slate-300"
                    disabled={posting}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            
            <p className="text-xs text-slate-500 mt-4">
              Location will stay active for 30 days or until you remove it
            </p>
          </div>
        </div>
      ) : loading ? (
        // Loading state
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mb-4" />
          <p className="text-slate-400">Loading nearby bladers...</p>
        </div>
      ) : nearbyPlayers.length === 0 ? (
        // No players nearby
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-center max-w-md">
            <div className="w-24 h-24 mx-auto mb-6 bg-slate-700/30 rounded-full flex items-center justify-center">
              <MapPin className="w-12 h-12 text-slate-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-3">No Bladers Nearby</h2>
            <p className="text-slate-400">
              No other bladers have posted their location within 25 miles of you yet.
              Check back later!
            </p>
          </div>
        </div>
      ) : (
        // Bladers list
        <div className="space-y-3">
          <p className="text-slate-400 text-sm mb-4">
            Nearby Bladers: {nearbyPlayers.length} within 25 miles
          </p>
          
          {nearbyPlayers.map((player) => (
            <div 
              key={player.user_id}
              className="leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-3 relative"
              style={(player.banner_image && player.banner_image.trim() !== '') ? {
                backgroundImage: `url(${player.banner_image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              } : {}}
            >
              {/* Overlay for better text visibility when banner is set */}
              {player.banner_image && player.banner_image.trim() !== '' && (
                <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
              )}
              
              {/* Level Badge */}
              <LevelBadge level={player.level || 1} small={true} verified={player.has_made_first_purchase} />
              
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Profile Picture */}
                  {player.profile_picture ? (
                    <img
                      src={player.profile_picture}
                      alt={player.username}
                      className="w-12 h-12 rounded-full object-cover border-2"
                      style={{ borderColor: player.circle_color || '#475569' }}
                    />
                  ) : (
                    <div 
                      className="w-12 h-12 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-300 text-sm font-semibold"
                      style={{ borderColor: player.circle_color || '#475569' }}
                    >
                      {player.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                  
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${(player.banner_image && player.banner_image.trim() !== '') ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>
                        {player.username}
                      </span>
                      <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                        Let's battle!
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className={`text-sm ${(player.banner_image && player.banner_image.trim() !== '') ? 'text-white/80 drop-shadow' : 'text-slate-400'}`}>
                        📍 {player.distance} miles away
                      </span>
                      <span className={`text-sm digital-font ${(player.banner_image && player.banner_image.trim() !== '') ? 'text-cyan-300 drop-shadow' : 'text-cyan-400'}`}>
                        {player.battle_points} BP
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Message Button */}
                <Button
                  onClick={() => handleMessagePlayer(player.username)}
                  size="sm"
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-1" />
                  Message
                </Button>
              </div>
              
              {/* Trophy System - Top Right */}
              <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                <div className="flex items-center gap-0.5">
                  <Trophy className="w-3 h-3 text-yellow-500" />
                  <span className="text-[10px] font-bold text-white drop-shadow">{player.gold_trophies || 0}</span>
                </div>
                <div className="flex items-center gap-0.5">
                  <Trophy className="w-3 h-3 text-slate-300" />
                  <span className="text-[10px] font-bold text-white drop-shadow">{player.silver_trophies || 0}</span>
                </div>
                <div className="flex items-center gap-0.5">
                  <Trophy className="w-3 h-3 text-amber-600" />
                  <span className="text-[10px] font-bold text-white drop-shadow">{player.bronze_trophies || 0}</span>
                </div>
              </div>
              
              {/* EXP Progress Bar */}
              <ExpProgressBar level={player.level || 1} exp={player.exp || 0} />
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default Map;
